import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  Renderer2,
  ChangeDetectionStrategy,
  Inject,
  PLATFORM_ID
} from '@angular/core'
import { isPlatformBrowser } from '@angular/common'
import { Subject, merge, fromEvent, combineLatest, ReplaySubject } from 'rxjs'
import {
  filter,
  map,
  switchMap,
  mapTo,
  shareReplay,
  take,
  withLatestFrom,
  takeUntil,
  startWith,
  tap,
  scan
} from 'rxjs/operators'
import { notNullOrUndefined } from 'src/app/shared/functions/type-guards'

@Component({
  selector: 'flo-watch-carousel',
  templateUrl: './watch-carousel.component.html',
  styleUrls: ['./watch-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WatchCarouselComponent implements AfterViewInit, OnDestroy {
  constructor(
    private readonly renderer2: Renderer2,
    // tslint:disable-next-line:ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private scrollElement$ = new ReplaySubject<HTMLDivElement>(1)
  private carouselContainer$ = new ReplaySubject<HTMLDivElement>(1)
  public scrollClick$ = new Subject<number>()
  public ngAfterViewInit$ = new Subject<void>()
  public ngOnDestroy$ = new Subject<void>()

  @ViewChild('scrollContainer')
  public set scrollContainer(el: ElementRef<HTMLDivElement>) {
    if (!!el.nativeElement) {
      this.scrollElement$.next(el.nativeElement)
    }
  }

  @ViewChild('carouselContainer', { static: true })
  public set carouselContainer(el: ElementRef<HTMLDivElement>) {
    if (!!el.nativeElement) {
      this.carouselContainer$.next(el.nativeElement)
    }
  }

  private elements$ = this.scrollElement$.pipe(
    filter(el => el.children.length > 0),
    filter(el => notNullOrUndefined(el.children)),
    map(el => Array.from(el.children))
  )

  private computedPaddingChildElements$ = this.elements$.pipe(
    filter(() => isPlatformBrowser(this.platformId)),
    map(elements => {
      const computedStyles = getComputedStyle(elements[0])
      const computedRightPadding = !!computedStyles.paddingRight ? parseFloat(computedStyles.paddingRight) : 0
      return computedRightPadding
    }),
    shareReplay(1)
  )

  private transformAmount$ = this.scrollClick$.pipe(scan((acc, curr) => acc + curr, 0))

  public scrollPosition$ = this.transformAmount$.pipe(
    withLatestFrom(this.scrollElement$, this.computedPaddingChildElements$),
    map(([transformAmount, scrollElement, computedPadding]) => {
      const endScrollPosition = scrollElement.scrollWidth - (scrollElement.clientWidth + computedPadding)
      // ensure we never scroll past the starting point
      if (transformAmount < 0) {
        return 0
      }

      if (transformAmount >= endScrollPosition) {
        return endScrollPosition
      } else {
        return transformAmount
      }
    }),
    shareReplay(1)
  )

  public showScrollControls$ = this.carouselContainer$.pipe(
    switchMap(elm => {
      return combineLatest([
        merge(fromEvent(elm, 'mouseenter').pipe(mapTo(true)), fromEvent(elm, 'mouseleave').pipe(mapTo(false))),
        this.scrollElement$,
        this.scrollPosition$.pipe(startWith(0)),
        this.computedPaddingChildElements$
      ])
    }),
    map(([mouseEvent, scrollElement, scrollPosition, computedPadding]) => {
      const endScrollPosition = scrollElement.scrollWidth - (scrollElement.clientWidth + computedPadding)
      return {
        showLeft: scrollPosition !== 0,
        showRight: scrollPosition < endScrollPosition,
        clientWidth: scrollElement.clientWidth + computedPadding
      }
    }),
    shareReplay(1)
  )

  public addScrollSnapClass$ = this.elements$.pipe(take(1)).subscribe(elements => {
    elements.forEach((element, i) => {
      this.renderer2.addClass(element, 'scroll-snap-by-device')
    })
  })

  // 'scrolling' with transform: translateX() to ensure animation/smooth scroll across browsers
  public scroll$ = this.scrollPosition$
    .pipe(withLatestFrom(this.scrollElement$), takeUntil(this.ngOnDestroy$))
    .subscribe(([position, scrollElement]) => {
      this.renderer2.setStyle(scrollElement, 'transform', `translateX(${-position}px)`)
    })

  public ngAfterViewInit(): void {
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
